import { Editor, Path, Transforms } from 'slate';
import { ReactEditor } from 'slate-react';
import React from 'react';
import { v4 as uuid4 } from 'uuid';
import { icons } from '@ublend-npm/aulaui-next';
import { insertVoidElement } from '../../lib/commands';
import { isH5pElement, h5pType } from './h5pType';
import { AulaPlugin } from '../plugins';
import { LtiBlock } from '../../components/LtiBlock/LtiBlock';
import { AulaEditorContext, AulaEditorInstance } from '../../types';
import { NestedMenuItem } from '../../components/NestedMenu/NestedMenu';

const IFRAME_MESSAGE_PREFIX = 'aulaH5pContextDelivery|';
const ltiEndpoint = 'h5p-lti-consumer';

export const insertH5p = (
  editor: AulaEditorInstance,
  path: Path,
  context?: Omit<AulaEditorContext, 'entities'>
) => {
  if (context == null) {
    throw new Error('context undefined');
  }

  const { createEntity } = context;
  const linkId = uuid4();
  const entity = createEntity({
    type: 'lti',
    linkId,
    params: { launchType: 'content-selection' },
  });

  /**
   * A timeout is used here to prevent loss of focus
   * when using the keyboard to insert the block (via the block menu)
   */
  setTimeout(() => {
    // insert at the end of current block
    const end = Editor.end(editor, path);
    Transforms.select(editor, end);

    insertVoidElement(editor, {
      type: h5pType,
      linkId,
      entity,
    });
  }, 0);
};

export const h5p: AulaPlugin = (editableProps, editor, context) => {
  const { isVoid } = editor;
  const title = 'H5p';
  const icon = <icons.H5p />;

  const { updateEntity } = context as AulaEditorContext;

  editor.isVoid = (element) => {
    if (element.type === h5pType) return true;

    return isVoid(element);
  };

  return {
    ...editableProps,
    blockMenu: {
      ...editableProps.blockMenu,
      Integrations: {
        ...editableProps.blockMenu?.Integrations,
        H5p: ({ text, element, attributes, onClose }) => (
          <NestedMenuItem
            leftIcon={icon}
            key={text}
            text={text}
            attributes={{
              ...attributes,
              onClick: () => {
                onClose?.();
                insertH5p(
                  editor,
                  ReactEditor.findPath(editor, element),
                  context
                );
              },
            }}
          />
        ),
      },
    },
    renderSubMenuItem: (props) =>
      // eslint-disable-next-line react/destructuring-assignment
      props.text === 'Integrations' ? (
        <NestedMenuItem
          leftIcon={<icons.ExtensionIcon />}
          rightIcon={<icons.ArrowRight />}
          {...props}
        />
      ) : (
        editableProps?.renderSubMenuItem?.(props) || (
          <NestedMenuItem {...props} />
        )
      ),

    renderElement: (props) => {
      const { element, attributes, children } = props;

      if (!isH5pElement(element)) {
        return editableProps.renderElement?.(props) || <p />;
      }

      const onMessage = (data: any) => {
        if (element.entity !== null) {
          updateEntity(element.entity, {
            data,
            params: {
              launchType: 'basic',
              contentId: data.contentId,
            },
          });
        }
      };

      return (
        <div {...attributes}>
          <LtiBlock
            title={title}
            icon={icon}
            scrolling
            element={element as any}
            ltiEndpoint={ltiEndpoint}
            onMessage={onMessage}
            messagePrefix={IFRAME_MESSAGE_PREFIX}
          />
          {children}
        </div>
      );
    },
  };
};
